<template>
  <div class="mobile-task-page">
    <main class="phone-shell">
      <nav class="h5-topbar" aria-label="手机任务导航">
        <button type="button" aria-label="返回 H5 首页" title="返回 H5 首页" @click="goMobilePortal">
          <el-icon><ArrowLeft /></el-icon>
        </button>
        <span>
          <strong>Mission Composer</strong>
          <small>DeRAS Mobile</small>
        </span>
        <span :class="['network-state', { live: isAuthenticated }]">
          <i></i>{{ isAuthenticated ? 'LIVE' : 'PREVIEW' }}
        </span>
      </nav>

      <section v-if="!hasPageAccess" class="access-gate">
        <img :src="heroImage" alt="机器人任务执行现场" />
        <div class="gate-mask">
          <span>AUTHORIZATION REQUIRED</span>
          <h1>登录后发布真实任务</h1>
          <p>任务会写入 DeRAS 平台，并同步显示在电脑端的任务中心。</p>
          <button type="button" @click="goMobilePortal">
            前往登录
            <el-icon><ArrowRight /></el-icon>
          </button>
        </div>
      </section>

      <template v-else>
        <header class="mission-header">
          <img :src="heroImage" alt="机器人任务发布" />
          <div class="mission-header-mask">
            <div class="mission-meta">
              <span><i></i> FIELD MISSION</span>
              <strong>{{ isAuthenticated ? 'ACCOUNT SYNCED' : 'UI PREVIEW' }}</strong>
            </div>
            <div>
              <span class="eyebrow">ROBOCOIN DERAS</span>
              <h1>发布机器人任务</h1>
              <p>地图选点、执行设备、分包属性与任务预估在手机端一次完成。</p>
            </div>
          </div>
        </header>

        <div v-if="previewMode" class="preview-banner">
          <el-icon><WarningFilled /></el-icon>
          <span>当前为界面预览。填写内容不会保存，登录正式账户后才能发布任务。</span>
        </div>

        <section class="progress-console">
          <div class="progress-head">
            <div>
              <span>MISSION BUILD</span>
              <strong>{{ paddedStep }} / {{ String(steps.length).padStart(2, '0') }}</strong>
            </div>
            <em>{{ activeStep.short }}</em>
          </div>
          <div class="progress-track"><i :style="{ width: progressPercent + '%' }"></i></div>
          <div class="step-rail" aria-label="任务步骤">
            <button
              v-for="(step, index) in steps"
              :key="step.key"
              type="button"
              :class="{ active: index + 1 === currentStep, done: index + 1 < currentStep }"
              :disabled="index + 1 > maxVisitedStep"
              :title="step.short"
              @click="goToStep(index + 1)"
            >
              {{ String(index + 1).padStart(2, '0') }}
            </button>
          </div>
        </section>

        <transition name="step-slide" mode="out-in">
          <section :key="activeStep.key" class="step-panel">
            <div class="step-title">
              <span>{{ activeStep.kicker }}</span>
              <h2>{{ activeStep.title }}</h2>
              <p>{{ activeStep.tip }}</p>
            </div>

            <div v-if="activeStep.key === 'serviceType'" class="choice-grid service-grid">
              <button
                v-for="item in serviceTypes"
                :key="item.value"
                :class="{ selected: form.serviceType === item.value }"
                type="button"
                @click="selectAndRefresh('serviceType', item.value)"
              >
                <span>{{ item.badge }}</span>
                <strong>{{ item.label }}</strong>
                <em>{{ item.text }}</em>
              </button>
            </div>

            <div v-else-if="activeStep.key === 'robotType'" class="choice-grid robot-grid">
              <button
                v-for="item in robotTypes"
                :key="item.value"
                :class="{ selected: form.robotType === item.value }"
                type="button"
                @click="selectAndRefresh('robotType', item.value)"
              >
                <span>{{ item.short }}</span>
                <strong>{{ item.label }}</strong>
                <em>{{ item.text }}</em>
              </button>
            </div>

            <div v-else-if="activeStep.key === 'go2Action'">
              <div class="choice-grid action-grid">
                <button
                  v-for="item in go2ActionTypes"
                  :key="item.value"
                  :class="{ selected: form.go2Action === item.value }"
                  type="button"
                  @click="selectGo2Action(item.value)"
                >
                  <span>G2</span>
                  <strong>{{ item.label }}</strong>
                  <em>{{ item.text }}</em>
                </button>
              </div>
              <div v-if="form.go2Action === 'PATROL_INSPECTION'" class="parameter-readout">
                <span>PRESET ROUTE</span>
                <strong>前进 10 m → 旋转 360° → 前进 10 m</strong>
              </div>
              <div v-if="['MOVE_FORWARD', 'MOVE_BACKWARD'].includes(form.go2Action)" class="field-stack action-parameter">
                <label>
                  <span>移动距离（米）</span>
                  <input v-model.number="form.go2ActionMeters" type="number" min="0.2" max="20" step="0.5" />
                </label>
              </div>
              <div v-if="form.go2Action === 'TURN_AROUND'" class="field-stack action-parameter">
                <label>
                  <span>旋转时长（秒）</span>
                  <input v-model.number="form.go2ActionSeconds" type="number" min="1" max="12" step="1" />
                </label>
              </div>
            </div>

            <div v-else-if="activeStep.key === 'title'" class="field-stack">
              <label>
                <span>任务标题</span>
                <input v-model.trim="form.title" type="text" maxlength="120" placeholder="例如：园区 A 栋夜间巡检" @keyup.enter="nextStep" />
              </label>
            </div>

            <div v-else-if="activeStep.key === 'location'" class="field-stack location-stack">
              <template v-if="form.serviceType === 'SHUTTLE'">
                <label>
                  <span>起点</span>
                  <address-picker
                    v-model="form.beginAddress"
                    :lat="form.beginLat"
                    :lng="form.beginLng"
                    placeholder="在地图上选择起点"
                    dialog-title="选择任务起点"
                    search-placeholder="输入起点关键词"
                    @change="applyLocation('begin', $event)"
                  />
                </label>
                <label>
                  <span>终点</span>
                  <address-picker
                    v-model="form.endAddress"
                    :lat="form.endLat"
                    :lng="form.endLng"
                    placeholder="在地图上选择终点"
                    dialog-title="选择任务终点"
                    search-placeholder="输入终点关键词"
                    @change="applyLocation('end', $event)"
                  />
                </label>
                <div class="route-readout">
                  <span>ROUTE DISTANCE</span>
                  <strong>{{ routeDistanceText }}</strong>
                </div>
              </template>
              <label v-else>
                <span>服务地址</span>
                <address-picker
                  v-model="form.serviceAddress"
                  :lat="form.serviceLat"
                  :lng="form.serviceLng"
                  placeholder="在地图上选择服务地址"
                  dialog-title="选择服务地址"
                  search-placeholder="输入服务地址关键词"
                  @change="applyLocation('service', $event)"
                />
              </label>
            </div>

            <div v-else-if="activeStep.key === 'scheduleTime'" class="field-stack">
              <label>
                <span>预约开始时间</span>
                <input v-model="form.scheduleTime" type="datetime-local" />
              </label>
            </div>

            <div v-else-if="activeStep.key === 'contactPhone'" class="field-stack">
              <label>
                <span>联系电话</span>
                <input v-model.trim="form.contactPhone" type="tel" maxlength="32" placeholder="手机号或国际联系电话" @keyup.enter="nextStep" />
              </label>
            </div>

            <div v-else-if="activeStep.key === 'message'" class="field-stack">
              <label>
                <span>任务说明</span>
                <textarea v-model.trim="form.message" rows="6" maxlength="1000" placeholder="现场要求、注意事项、交付标准"></textarea>
              </label>
              <small class="character-count">{{ form.message.length }} / 1000</small>
            </div>

            <div v-else-if="activeStep.key === 'subcontract'" class="choice-grid split-grid">
              <button
                type="button"
                :class="{ selected: form.allowSubcontract === 0 }"
                @click="form.allowSubcontract = 0"
              >
                <span>01</span>
                <strong>整单履约</strong>
                <em>禁止分包，由同一个机器人完成全部任务。</em>
              </button>
              <button
                type="button"
                :class="{ selected: form.allowSubcontract === 1 }"
                @click="form.allowSubcontract = 1"
              >
                <span>02</span>
                <strong>允许两段协作</strong>
                <em>接单时必须确定第二段机器人和交接方案，不允许执行途中临时转包。</em>
              </button>
            </div>

            <div v-else class="review-list">
              <div class="review-head">
                <span>FINAL CHECK</span>
                <h2>确认任务清单</h2>
                <p>提交后会进入正式任务中心，并按平台现有审核与派单规则流转。</p>
              </div>
              <button v-for="item in reviewItems" :key="item.key" type="button" @click="goToStep(item.step)">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <em>修改</em>
              </button>
            </div>
          </section>
        </transition>

        <section :class="['estimate-console', { loading: estimateLoading }]">
          <div>
            <span>PLATFORM ESTIMATE</span>
            <strong>{{ estimatedRobocoin }} <small>RoboCoin</small></strong>
          </div>
          <p>{{ estimateSummary }}</p>
          <i></i>
        </section>

        <div v-if="submissionError" class="submission-error" role="alert">
          <el-icon><WarningFilled /></el-icon>
          <span>{{ submissionError }}</span>
        </div>

        <nav class="mobile-actions">
          <button type="button" class="ghost" :disabled="currentStep === 1 || submitting" @click="prevStep">
            <el-icon><ArrowLeft /></el-icon>
            上一步
          </button>
          <button v-if="!isReviewStep" type="button" class="primary" :disabled="submitting" @click="nextStep">
            下一步
            <el-icon><ArrowRight /></el-icon>
          </button>
          <button v-else type="button" class="primary" :disabled="submitting" @click="submitTask">
            <el-icon><Promotion /></el-icon>
            {{ submitting ? '正在发布...' : (isAuthenticated ? '正式发布任务' : '登录后发布') }}
          </button>
        </nav>
      </template>
    </main>

    <transition name="toast-pop">
      <div v-if="toastMessage" class="toast-message" role="status">{{ toastMessage }}</div>
    </transition>

    <transition name="sheet-up">
      <section v-if="successVisible" class="success-sheet">
        <div>
          <span class="success-code">MISSION ACCEPTED</span>
          <div class="success-mark"><el-icon><CircleCheckFilled /></el-icon></div>
          <h2>任务已发布到平台</h2>
          <p>电脑端会读取到同一条任务，后续按现有审核、派单和接单规则流转。</p>
          <strong v-if="publishedTaskId">TASK ID / {{ publishedTaskId }}</strong>
          <div class="success-actions">
            <button type="button" class="ghost-action" @click="goMobilePortal">返回工作台</button>
            <button type="button" @click="resetAfterSubmit">继续发布</button>
          </div>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import {
  ArrowLeft,
  ArrowRight,
  CircleCheckFilled,
  Promotion,
  WarningFilled
} from '@element-plus/icons-vue'
import { getApiErrorMessage } from '../api/http'
import { getTaskEstimate, publishTask } from '../api/task-publish'
import { isValidInternationalPhone, normalizePhone } from '../utils/phone'
import AddressPicker from '../components/AddressPicker.vue'
const heroImage = '/img/faburwu.1c6ffc65.png'
import { getAuthUser, getToken } from '../utils/auth'

const PREVIEW_KEY = 'roc_mobile_preview'

export default {
  name: 'MobileTaskPublish',
  components: {
    AddressPicker,
    ArrowLeft,
    ArrowRight,
    CircleCheckFilled,
    Promotion,
    WarningFilled
  },
  data() {
    return {
      heroImage,
      currentUser: getAuthUser() || {},
      authToken: getToken() || '',
      previewMode: window.sessionStorage.getItem(PREVIEW_KEY) === '1',
      currentStep: 1,
      maxVisitedStep: 1,
      form: this.createEmptyForm(),
      estimateLoading: false,
      estimateInfo: this.createDefaultEstimate(),
      estimateError: '',
      submitting: false,
      submissionError: '',
      toastMessage: '',
      toastTimer: null,
      successVisible: false,
      publishedTaskId: '',
      serviceTypes: [
        { value: 'HOME_SERVICE', label: '定点服务', badge: '到点', text: '机器人到指定位置完成清洁、巡检或配送。' },
        { value: 'SHUTTLE', label: '路线接驳', badge: '路线', text: '机器人按确认的起点和终点执行接驳。' }
      ],
      robotTypes: [
        { value: 'CLEANING', label: '清洁机器人', short: '扫', text: '清扫、保洁' },
        { value: 'DELIVERY', label: '配送机器人', short: '配', text: '物品配送、转运' },
        { value: 'SHUTTLE', label: '接驳机器人', short: '驳', text: '短途接驳服务' },
        { value: 'DRONE', label: '无人机', short: '飞', text: '空中巡航、查看' },
        { value: 'INSPECTION', label: '巡检机器人', short: '巡', text: '巡逻、异常发现' },
        { value: 'GO2', label: 'Unitree Go2', short: 'G2', text: '动作与短距移动' }
      ],
      go2ActionTypes: [
        { value: 'STAND_UP', label: '起立', text: '切换到稳定站立状态' },
        { value: 'HEART', label: '比心', text: '执行预置比心动作' },
        { value: 'PATROL_INSPECTION', label: '巡检演示', text: '10 米、旋转、再前进 10 米' },
        { value: 'DANCE_1', label: '跳舞 1', text: '执行第一套预置舞蹈' },
        { value: 'DANCE_2', label: '跳舞 2', text: '执行第二套预置舞蹈' },
        { value: 'MOVE_FORWARD', label: '向前移动', text: '按填写距离向前移动' },
        { value: 'MOVE_BACKWARD', label: '向后移动', text: '按填写距离向后移动' },
        { value: 'TURN_AROUND', label: '原地旋转', text: '按填写时长原地旋转' },
        { value: 'STOP', label: '停止动作', text: '停止当前移动指令' }
      ]
    }
  },
  computed: {
    isAuthenticated() {
      return Boolean(this.authToken && this.currentUser && this.currentUser.id)
    },
    hasPageAccess() {
      return this.isAuthenticated || this.previewMode
    },
    steps() {
      return [
        { key: 'serviceType', short: '服务方式', kicker: '01 / SERVICE', title: '选择服务方式', tip: '确定机器人执行定点服务，还是按路线完成接驳。' },
        { key: 'robotType', short: '机器人类型', kicker: '02 / ROBOT', title: '选择执行设备', tip: '一次展示全部机器人类型，选中后继续。' },
        ...(this.form.robotType === 'GO2' ? [{ key: 'go2Action', short: 'Go2 动作', kicker: '02A / ACTION', title: '配置机器狗动作', tip: '动作参数会随任务一起发送给 SDK Agent。' }] : []),
        { key: 'title', short: '任务标题', kicker: '03 / TITLE', title: '描述这次任务', tip: '用一句话让接单方快速理解任务目标。' },
        { key: 'location', short: '地图选点', kicker: '04 / LOCATION', title: this.form.serviceType === 'SHUTTLE' ? '确认任务路线' : '确认服务位置', tip: '通过地图搜索并保存 WGS84 坐标，电脑端会显示同一位置数据。' },
        { key: 'scheduleTime', short: '预约时间', kicker: '05 / TIME', title: '设置开始时间', tip: '选择机器人应当开始执行任务的时间。' },
        { key: 'contactPhone', short: '联系方式', kicker: '06 / CONTACT', title: '填写任务联系人', tip: '用于接单与执行过程中的必要确认。' },
        { key: 'message', short: '任务说明', kicker: '07 / DETAILS', title: '补充执行要求', tip: '写明现场限制、注意事项和交付标准。' },
        { key: 'subcontract', short: '分包属性', kicker: '08 / COOPERATION', title: '是否允许任务分包', tip: '允许分包时，接单方必须在接单阶段确定第二段机器人。' },
        { key: 'review', short: '确认发布', kicker: '09 / REVIEW', title: '确认任务清单', tip: '提交前检查任务信息和平台预估。' }
      ]
    },
    activeStep() {
      return this.steps[this.currentStep - 1] || this.steps[0]
    },
    paddedStep() {
      return String(this.currentStep).padStart(2, '0')
    },
    progressPercent() {
      return Math.round((this.currentStep / this.steps.length) * 100)
    },
    isReviewStep() {
      return this.activeStep.key === 'review'
    },
    estimatedRobocoin() {
      const value = Number(this.estimateInfo.estimatedRobocoin)
      return Number.isFinite(value) ? value.toFixed(2) : '0.00'
    },
    estimateSummary() {
      if (this.estimateLoading) return '正在读取平台预估...'
      if (this.estimateError) return this.estimateError
      const minutes = Number(this.estimateInfo.estimatedServiceMinutes || 0)
      const duration = minutes > 0 ? `约 ${minutes} 分钟` : '时长待计算'
      return `${duration} · ${this.estimateInfo.message || '平台按任务信息计算补助额度。'}`
    },
    routeDistanceMeters() {
      const calculated = this.calculateRouteDistanceMeters()
      if (Number.isFinite(calculated) && calculated > 0) return calculated
      const estimated = Number(this.estimateInfo.routeDistanceMeters)
      return Number.isFinite(estimated) && estimated > 0 ? estimated : null
    },
    routeDistanceText() {
      const meters = this.routeDistanceMeters
      if (!meters) return '选择起点和终点后计算'
      return meters < 1000 ? `${Math.round(meters)} 米` : `${(meters / 1000).toFixed(2)} 公里`
    },
    locationText() {
      if (this.form.serviceType === 'SHUTTLE') {
        return `${this.form.beginAddress || '未选择起点'} → ${this.form.endAddress || '未选择终点'}`
      }
      return this.form.serviceAddress || '未选择服务地址'
    },
    reviewItems() {
      return [
        { key: 'serviceType', label: '服务方式', value: this.serviceTypeLabel(this.form.serviceType), step: this.stepNumber('serviceType') },
        { key: 'robotType', label: '机器人类型', value: this.robotTypeLabel(this.form.robotType), step: this.stepNumber('robotType') },
        ...(this.form.robotType === 'GO2' ? [{ key: 'go2Action', label: 'Go2 动作', value: this.go2ActionText(), step: this.stepNumber('go2Action') }] : []),
        { key: 'title', label: '任务标题', value: this.form.title || '未填写', step: this.stepNumber('title') },
        { key: 'location', label: '服务位置', value: this.locationText, step: this.stepNumber('location') },
        { key: 'scheduleTime', label: '预约时间', value: this.form.scheduleTime ? this.form.scheduleTime.replace('T', ' ') : '未选择', step: this.stepNumber('scheduleTime') },
        { key: 'contactPhone', label: '联系方式', value: this.form.contactPhone || '未填写', step: this.stepNumber('contactPhone') },
        { key: 'message', label: '任务说明', value: this.form.message || '未填写', step: this.stepNumber('message') },
        { key: 'subcontract', label: '分包属性', value: this.form.allowSubcontract === 1 ? '允许接单时确定两段协作' : '禁止分包，整单履约', step: this.stepNumber('subcontract') },
        { key: 'estimate', label: '平台预估', value: `${this.estimatedRobocoin} RoboCoin`, step: this.stepNumber('review') }
      ]
    }
  },
  created() {
    if (this.hasPageAccess) this.refreshEstimate()
  },
  beforeUnmount() {
    if (this.toastTimer) window.clearTimeout(this.toastTimer)
  },
  methods: {
    createEmptyForm() {
      return {
        serviceType: '',
        robotType: '',
        allowSubcontract: 0,
        go2Action: '',
        go2ActionMeters: 1,
        go2ActionSeconds: null,
        title: '',
        serviceAddress: '',
        serviceLat: null,
        serviceLng: null,
        beginAddress: '',
        beginLat: null,
        beginLng: null,
        endAddress: '',
        endLat: null,
        endLng: null,
        scheduleTime: '',
        contactPhone: '',
        message: ''
      }
    },
    createDefaultEstimate() {
      return {
        estimatedRobocoin: 3,
        estimatedServiceMinutes: 120,
        estimateSource: 'DEFAULT',
        robocoinHourlyRate: 1.5,
        routeDistanceMeters: null,
        robocoinDistanceRate: 0,
        message: '暂无历史数据时按平台默认服务时长预估。'
      }
    },
    goMobilePortal() {
      this.$router.push({ name: 'mobilePortal' })
    },
    selectAndRefresh(field, value) {
      this.form[field] = value
      if (field === 'serviceType') {
        this.form.serviceAddress = ''
        this.form.serviceLat = null
        this.form.serviceLng = null
        this.form.beginAddress = ''
        this.form.beginLat = null
        this.form.beginLng = null
        this.form.endAddress = ''
        this.form.endLat = null
        this.form.endLng = null
      }
      if (field === 'robotType' && value !== 'GO2') {
        this.form.go2Action = ''
        this.form.go2ActionMeters = 1
        this.form.go2ActionSeconds = null
      }
      this.refreshEstimate()
    },
    selectGo2Action(value) {
      this.form.go2Action = value
      this.form.go2ActionMeters = ['MOVE_FORWARD', 'MOVE_BACKWARD', 'PATROL_INSPECTION'].includes(value)
        ? (value === 'PATROL_INSPECTION' ? 10 : (Number(this.form.go2ActionMeters) > 0 ? this.form.go2ActionMeters : 1))
        : null
      this.form.go2ActionSeconds = ['TURN_AROUND', 'PATROL_INSPECTION'].includes(value)
        ? (value === 'PATROL_INSPECTION' ? 9 : (Number(this.form.go2ActionSeconds) > 0 ? this.form.go2ActionSeconds : 4))
        : null
    },
    applyLocation(prefix, payload) {
      const fieldMap = {
        service: ['serviceAddress', 'serviceLat', 'serviceLng'],
        begin: ['beginAddress', 'beginLat', 'beginLng'],
        end: ['endAddress', 'endLat', 'endLng']
      }
      const fields = fieldMap[prefix]
      if (!fields || !payload) return
      this.form[fields[0]] = payload.address
      this.form[fields[1]] = payload.lat
      this.form[fields[2]] = payload.lng
      this.refreshEstimate()
    },
    validateCurrentStep() {
      const key = this.activeStep.key
      if (key === 'serviceType' && !this.form.serviceType) return '请选择服务方式。'
      if (key === 'robotType' && !this.form.robotType) return '请选择机器人类型。'
      if (key === 'go2Action' && !this.form.go2Action) return '请选择 Go2 动作。'
      if (key === 'go2Action' && ['MOVE_FORWARD', 'MOVE_BACKWARD', 'PATROL_INSPECTION'].includes(this.form.go2Action) && !(Number(this.form.go2ActionMeters) > 0)) return '请填写有效的移动距离。'
      if (key === 'title' && !this.form.title) return '请填写任务标题。'
      if (key === 'location') {
        if (this.form.serviceType === 'SHUTTLE' && (!this.hasCoordinates('begin') || !this.hasCoordinates('end'))) return '请在地图上确认起点和终点。'
        if (this.form.serviceType !== 'SHUTTLE' && !this.hasCoordinates('service')) return '请在地图上确认服务地址。'
      }
      if (key === 'scheduleTime') {
        if (!this.form.scheduleTime) return '请选择预约时间。'
        const timestamp = new Date(this.form.scheduleTime).getTime()
        if (!Number.isFinite(timestamp)) return '预约时间格式不正确。'
        if (timestamp <= Date.now()) return '预约时间必须晚于当前时间。'
      }
      if (key === 'contactPhone') {
        if (!normalizePhone(this.form.contactPhone)) return '请填写联系方式。'
        if (!isValidInternationalPhone(this.form.contactPhone)) return '请输入 7 至 15 位有效电话号码，可包含国际区号。'
      }
      if (key === 'message' && !this.form.message) return '请填写任务说明。'
      return ''
    },
    hasCoordinates(prefix) {
      const lat = this.form[`${prefix}Lat`]
      const lng = this.form[`${prefix}Lng`]
      return lat !== null && lat !== '' && lng !== null && lng !== '' && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
    },
    nextStep() {
      const message = this.validateCurrentStep()
      if (message) {
        this.showToast(message)
        return
      }
      this.currentStep = Math.min(this.currentStep + 1, this.steps.length)
      this.maxVisitedStep = Math.max(this.maxVisitedStep, this.currentStep)
      if (this.isReviewStep) this.refreshEstimate()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    prevStep() {
      this.currentStep = Math.max(this.currentStep - 1, 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    goToStep(step) {
      if (step > this.maxVisitedStep) return
      this.currentStep = Math.min(Math.max(step, 1), this.steps.length)
      this.successVisible = false
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    async refreshEstimate() {
      if (!this.isAuthenticated) {
        this.estimateInfo = this.createDefaultEstimate()
        this.estimateError = this.previewMode ? '预览模式使用默认预估，登录后读取平台实时结果。' : ''
        return
      }
      this.estimateLoading = true
      this.estimateError = ''
      try {
        const routeDistance = this.form.serviceType === 'SHUTTLE' ? this.calculateRouteDistanceMeters() : null
        const response = await getTaskEstimate({
          serviceType: this.form.serviceType || 'HOME_SERVICE',
          estimatedRobocoin: this.estimateInfo.estimatedRobocoin,
          routeDistanceMeters: Number.isFinite(routeDistance) && routeDistance > 0 ? Number(routeDistance.toFixed(2)) : undefined,
          estimatedServiceMinutes: this.estimateInfo.estimatedServiceMinutes || undefined,
          beginLat: this.form.beginLat || undefined,
          beginLng: this.form.beginLng || undefined,
          endLat: this.form.endLat || undefined,
          endLng: this.form.endLng || undefined
        })
        if (!response.data || response.data.code !== 200 || !response.data.data) {
          this.estimateError = (response.data && response.data.msg) || '预估接口未返回有效结果，当前使用默认预估。'
          return
        }
        this.estimateInfo = {
          ...this.estimateInfo,
          ...response.data.data
        }
      } catch (error) {
        this.estimateError = getApiErrorMessage(error, '预估读取失败，当前使用默认预估。')
      } finally {
        this.estimateLoading = false
      }
    },
    async submitTask() {
      for (let step = 1; step < this.steps.length; step += 1) {
        this.currentStep = step
        const message = this.validateCurrentStep()
        if (message) {
          this.showToast(message)
          return
        }
      }
      this.currentStep = this.steps.length
      this.maxVisitedStep = this.steps.length

      if (!this.isAuthenticated) {
        this.showToast('请先登录正式账户，再发布任务。')
        return
      }

      this.submitting = true
      this.submissionError = ''
      try {
        await this.refreshEstimate()
        const response = await publishTask(this.buildSubmitPayload())
        if (!response.data || response.data.code !== 200) {
          this.submissionError = (response.data && response.data.msg) || '任务发布失败，请检查任务信息后重试。'
          return
        }
        const data = response.data.data || {}
        this.publishedTaskId = data.taskId || data.id || ''
        this.successVisible = true
      } catch (error) {
        this.submissionError = getApiErrorMessage(error, '任务发布失败，请稍后重试。')
      } finally {
        this.submitting = false
      }
    },
    buildSubmitPayload() {
      const routeDistance = this.form.serviceType === 'SHUTTLE' ? this.routeDistanceMeters : null
      return {
        publisherUserId: this.currentUser.id,
        taskName: this.form.title,
        title: this.form.title,
        message: this.form.message,
        typeId: 1,
        serviceType: this.form.serviceType,
        expectedRobotType: this.form.robotType,
        allowSubcontract: this.form.allowSubcontract === 1 ? 1 : 0,
        go2Action: this.form.robotType === 'GO2' ? this.form.go2Action : null,
        go2ActionMeters: this.form.robotType === 'GO2' && ['MOVE_FORWARD', 'MOVE_BACKWARD', 'PATROL_INSPECTION'].includes(this.form.go2Action) ? this.form.go2ActionMeters : null,
        go2ActionSeconds: this.form.robotType === 'GO2' && ['TURN_AROUND', 'PATROL_INSPECTION'].includes(this.form.go2Action) ? this.form.go2ActionSeconds : null,
        serviceAddress: this.form.serviceType === 'HOME_SERVICE' ? this.form.serviceAddress : null,
        serviceLat: this.form.serviceType === 'HOME_SERVICE' ? this.form.serviceLat : null,
        serviceLng: this.form.serviceType === 'HOME_SERVICE' ? this.form.serviceLng : null,
        contactPhone: this.form.contactPhone,
        scheduleTime: this.normalizeScheduleTime(this.form.scheduleTime),
        beginAddress: this.form.serviceType === 'SHUTTLE' ? this.form.beginAddress : null,
        beginLat: this.form.serviceType === 'SHUTTLE' ? this.form.beginLat : null,
        beginLng: this.form.serviceType === 'SHUTTLE' ? this.form.beginLng : null,
        endAddress: this.form.serviceType === 'SHUTTLE' ? this.form.endAddress : null,
        endLat: this.form.serviceType === 'SHUTTLE' ? this.form.endLat : null,
        endLng: this.form.serviceType === 'SHUTTLE' ? this.form.endLng : null,
        estimatedRobocoin: Number(this.estimatedRobocoin),
        estimatedServiceMinutes: this.estimateInfo.estimatedServiceMinutes,
        estimateSource: this.estimateInfo.estimateSource,
        robocoinHourlyRate: this.estimateInfo.robocoinHourlyRate,
        routeDistanceMeters: routeDistance ? Number(routeDistance.toFixed(2)) : null,
        robocoinDistanceRate: this.form.serviceType === 'SHUTTLE' ? this.estimateInfo.robocoinDistanceRate : null,
        prepayStatus: 'PAID',
        paymentMethod: 'PLATFORM_SUBSIDY'
      }
    },
    normalizeScheduleTime(value) {
      if (!value) return ''
      const normalized = value.replace('T', ' ')
      return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(normalized) ? `${normalized}:00` : normalized
    },
    calculateRouteDistanceMeters() {
      if (!this.hasCoordinates('begin') || !this.hasCoordinates('end')) return null
      const toRadians = degrees => degrees * Math.PI / 180
      const lat1 = toRadians(Number(this.form.beginLat))
      const lat2 = toRadians(Number(this.form.endLat))
      const deltaLat = lat2 - lat1
      const deltaLng = toRadians(Number(this.form.endLng) - Number(this.form.beginLng))
      const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2
      return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    },
    resetAfterSubmit() {
      this.form = this.createEmptyForm()
      this.estimateInfo = this.createDefaultEstimate()
      this.currentStep = 1
      this.maxVisitedStep = 1
      this.successVisible = false
      this.publishedTaskId = ''
      this.submissionError = ''
      this.refreshEstimate()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    showToast(message) {
      this.toastMessage = message
      if (this.toastTimer) window.clearTimeout(this.toastTimer)
      this.toastTimer = window.setTimeout(() => {
        this.toastMessage = ''
        this.toastTimer = null
      }, 2600)
    },
    serviceTypeLabel(value) {
      const item = this.serviceTypes.find(entry => entry.value === value)
      return item ? item.label : '未选择'
    },
    robotTypeLabel(value) {
      const item = this.robotTypes.find(entry => entry.value === value)
      return item ? item.label : '未选择'
    },
    stepNumber(key) {
      const index = this.steps.findIndex(item => item.key === key)
      return index >= 0 ? index + 1 : this.steps.length
    },
    go2ActionText() {
      const item = this.go2ActionTypes.find(entry => entry.value === this.form.go2Action)
      const label = item ? item.label : '未选择'
      if (this.form.go2Action === 'PATROL_INSPECTION') return `${label} 10 m + 360° + 10 m`
      if (['MOVE_FORWARD', 'MOVE_BACKWARD'].includes(this.form.go2Action)) return `${label} ${this.form.go2ActionMeters || 1} m`
      if (this.form.go2Action === 'TURN_AROUND') return `${label} ${this.form.go2ActionSeconds || 4} s`
      return label
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

button,
input,
textarea {
  font: inherit;
  letter-spacing: 0;
}

button {
  -webkit-tap-highlight-color: transparent;
}

.mobile-task-page {
  min-height: 100vh;
  padding: 10px;
  color: #eef8f4;
  background-color: #070b0a;
  background-image:
    linear-gradient(rgba(93, 231, 205, 0.032) 1px, transparent 1px),
    linear-gradient(90deg, rgba(93, 231, 205, 0.032) 1px, transparent 1px);
  background-size: 28px 28px;
  font-family: "Noto Sans", system-ui, sans-serif;
}

.phone-shell {
  display: grid;
  align-content: start;
  gap: 10px;
  width: min(100%, 480px);
  min-height: calc(100vh - 20px);
  margin: 0 auto;
}

.h5-topbar {
  position: sticky;
  top: 8px;
  z-index: 20;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) auto;
  gap: 9px;
  align-items: center;
  min-height: 52px;
  padding: 6px;
  border: 1px solid rgba(128, 244, 214, 0.14);
  border-radius: 7px;
  background: rgba(7, 11, 10, 0.93);
  backdrop-filter: blur(16px);
}

.h5-topbar > button {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid rgba(238, 248, 244, 0.12);
  border-radius: 5px;
  color: #eef8f4;
  background: rgba(238, 248, 244, 0.04);
}

.h5-topbar > span:nth-child(2) {
  min-width: 0;
}

.h5-topbar strong,
.h5-topbar small {
  display: block;
}

.h5-topbar strong {
  overflow: hidden;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.h5-topbar small {
  margin-top: 2px;
  color: rgba(238, 248, 244, 0.4);
  font-size: 7px;
  text-transform: uppercase;
}

.network-state {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 6px;
  border: 1px solid rgba(246, 182, 72, 0.28);
  border-radius: 4px;
  color: #f6b648;
  font-size: 7px;
  font-weight: 900;
}

.network-state i,
.mission-meta i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 10px currentColor;
}

.network-state.live {
  color: #65e0ad;
  border-color: rgba(101, 224, 173, 0.3);
}

.access-gate,
.mission-header {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(128, 244, 214, 0.16);
  border-radius: 8px;
  background: #101715;
}

.access-gate {
  min-height: 520px;
}

.access-gate > img,
.mission-header > img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.access-gate > img {
  object-position: 47% center;
}

.gate-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 22px;
  background: linear-gradient(180deg, rgba(3, 7, 6, 0.08), rgba(3, 7, 6, 0.96));
}

.gate-mask > span,
.mission-meta,
.eyebrow,
.progress-head span,
.step-title > span,
.review-head > span,
.estimate-console > div > span,
.parameter-readout span,
.route-readout span {
  color: #61d9c8;
  font-size: 8px;
  font-weight: 900;
  text-transform: uppercase;
}

.gate-mask h1,
.mission-header h1,
.step-title h2,
.review-head h2,
.success-sheet h2 {
  margin: 0;
  letter-spacing: 0;
}

.gate-mask h1 {
  margin-top: 7px;
  font-size: 27px;
}

.gate-mask p {
  margin: 9px 0 18px;
  color: rgba(238, 248, 244, 0.66);
  font-size: 12px;
  line-height: 1.6;
}

.gate-mask button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 46px;
  border: 1px solid #69dec5;
  border-radius: 5px;
  color: #06110e;
  font-weight: 850;
  background: #69dec5;
}

.mission-header {
  height: 188px;
}

.mission-header > img {
  object-position: 53% 54%;
}

.mission-header-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 15px;
  background:
    linear-gradient(90deg, rgba(3, 7, 6, 0.9), rgba(3, 7, 6, 0.2) 78%),
    linear-gradient(180deg, rgba(3, 7, 6, 0.12), rgba(3, 7, 6, 0.88));
}

.mission-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.mission-meta > span {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mission-meta strong {
  color: rgba(238, 248, 244, 0.58);
  font-size: 7px;
}

.mission-header h1 {
  margin-top: 4px;
  font-size: 24px;
}

.mission-header p {
  max-width: 330px;
  margin: 6px 0 0;
  color: rgba(238, 248, 244, 0.64);
  font-size: 10px;
  line-height: 1.55;
}

.preview-banner,
.submission-error {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  padding: 10px 11px;
  border-radius: 5px;
  font-size: 10px;
  line-height: 1.5;
}

.preview-banner {
  color: #f6ca70;
  border: 1px solid rgba(246, 182, 72, 0.26);
  background: rgba(92, 58, 9, 0.26);
}

.progress-console,
.step-panel,
.estimate-console {
  border: 1px solid rgba(238, 248, 244, 0.1);
  border-radius: 7px;
  background: rgba(13, 21, 19, 0.88);
}

.progress-console {
  padding: 12px;
}

.progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.progress-head span,
.progress-head strong {
  display: block;
}

.progress-head strong {
  margin-top: 2px;
  font-size: 15px;
}

.progress-head em {
  color: rgba(238, 248, 244, 0.58);
  font-size: 10px;
  font-style: normal;
}

.progress-track {
  height: 3px;
  margin-top: 10px;
  overflow: hidden;
  background: rgba(238, 248, 244, 0.08);
}

.progress-track i {
  display: block;
  height: 100%;
  background: #69dec5;
  box-shadow: 0 0 10px rgba(105, 222, 197, 0.8);
  transition: width 0.24s ease;
}

.step-rail {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
}

.step-rail::-webkit-scrollbar {
  display: none;
}

.step-rail button {
  flex: 0 0 28px;
  width: 28px;
  height: 26px;
  padding: 0;
  border: 1px solid rgba(238, 248, 244, 0.11);
  border-radius: 3px;
  color: rgba(238, 248, 244, 0.32);
  font-size: 7px;
  background: rgba(238, 248, 244, 0.03);
}

.step-rail button.done {
  color: #65e0ad;
  border-color: rgba(101, 224, 173, 0.24);
}

.step-rail button.active {
  color: #06110e;
  border-color: #69dec5;
  background: #69dec5;
}

.step-panel {
  min-height: 390px;
  padding: 16px;
}

.step-title h2,
.review-head h2 {
  margin-top: 5px;
  font-size: 21px;
  line-height: 1.3;
}

.step-title p,
.review-head p {
  margin: 6px 0 0;
  color: rgba(238, 248, 244, 0.47);
  font-size: 10px;
  line-height: 1.55;
}

.choice-grid,
.field-stack,
.review-list {
  display: grid;
  gap: 9px;
  margin-top: 16px;
}

.service-grid,
.split-grid {
  grid-template-columns: 1fr;
}

.robot-grid,
.action-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.choice-grid > button {
  position: relative;
  min-width: 0;
  min-height: 112px;
  padding: 12px;
  border: 1px solid rgba(238, 248, 244, 0.11);
  border-radius: 6px;
  color: #eef8f4;
  text-align: left;
  background: rgba(6, 12, 10, 0.74);
}

.service-grid > button,
.split-grid > button {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 4px 11px;
  align-content: center;
  min-height: 84px;
}

.choice-grid > button::after {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 0;
  height: 2px;
  content: '';
  background: #69dec5;
  transition: width 0.2s ease;
}

.choice-grid > button.selected {
  border-color: rgba(105, 222, 197, 0.56);
  background: rgba(47, 146, 123, 0.16);
  box-shadow: inset 0 0 24px rgba(105, 222, 197, 0.04);
}

.choice-grid > button.selected::after {
  width: 52px;
}

.choice-grid span {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  margin-bottom: 8px;
  border: 1px solid rgba(246, 182, 72, 0.3);
  border-radius: 4px;
  color: #f6b648;
  font-size: 9px;
  font-weight: 900;
  background: rgba(246, 182, 72, 0.07);
}

.service-grid span,
.split-grid span {
  grid-row: 1 / span 2;
  width: max-content;
  min-width: 50px;
  max-width: 100%;
  padding: 0 8px;
  box-sizing: border-box;
  margin: 0;
  line-height: 1;
  white-space: nowrap;
}

.choice-grid button.selected span {
  color: #69dec5;
  border-color: rgba(105, 222, 197, 0.42);
  background: rgba(105, 222, 197, 0.08);
}

.choice-grid strong,
.choice-grid em {
  display: block;
}

.choice-grid strong {
  overflow-wrap: anywhere;
  font-size: 12px;
  line-height: 1.35;
}

.choice-grid em {
  margin-top: 4px;
  color: rgba(238, 248, 244, 0.4);
  font-size: 8px;
  font-style: normal;
  line-height: 1.45;
}

.service-grid em,
.split-grid em {
  margin-top: 0;
}

.parameter-readout,
.route-readout {
  margin-top: 10px;
  padding: 11px 12px;
  border-left: 2px solid #f6b648;
  background: rgba(246, 182, 72, 0.07);
}

.parameter-readout strong,
.route-readout strong {
  display: block;
  margin-top: 4px;
  color: rgba(238, 248, 244, 0.75);
  font-size: 10px;
}

.field-stack label,
.location-stack label {
  display: grid;
  gap: 7px;
}

.field-stack label > span {
  color: rgba(238, 248, 244, 0.64);
  font-size: 11px;
  font-weight: 750;
}

.field-stack input,
.field-stack textarea {
  width: 100%;
  min-height: 48px;
  padding: 12px;
  border: 1px solid rgba(128, 244, 214, 0.17);
  border-radius: 6px;
  color: #eef8f4;
  outline: none;
  background: rgba(6, 12, 10, 0.76);
}

.field-stack textarea {
  min-height: 130px;
  resize: vertical;
}

.field-stack input::placeholder,
.field-stack textarea::placeholder {
  color: rgba(238, 248, 244, 0.28);
}

.field-stack input:focus,
.field-stack textarea:focus {
  border-color: rgba(105, 222, 197, 0.58);
  box-shadow: 0 0 0 3px rgba(105, 222, 197, 0.08);
}

.action-parameter {
  margin-top: 10px;
}

.character-count {
  color: rgba(238, 248, 244, 0.3);
  font-size: 8px;
  text-align: right;
}

.location-stack :deep(.address-trigger) {
  border-color: rgba(128, 244, 214, 0.17);
  border-radius: 6px;
  background: rgba(6, 12, 10, 0.76);
}

.location-stack :deep(.address-trigger strong) {
  border-radius: 4px;
  background: #69dec5;
}

.review-list > button {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 4px 10px;
  width: 100%;
  padding: 11px 0;
  border: 0;
  border-bottom: 1px solid rgba(238, 248, 244, 0.09);
  color: #eef8f4;
  text-align: left;
  background: transparent;
}

.review-list > button > span {
  color: rgba(238, 248, 244, 0.38);
  font-size: 8px;
}

.review-list > button strong {
  grid-column: 1 / -1;
  overflow-wrap: anywhere;
  font-size: 11px;
  line-height: 1.45;
}

.review-list > button em {
  grid-row: 1;
  grid-column: 2;
  color: #f6b648;
  font-size: 8px;
  font-style: normal;
}

.estimate-console {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 13px;
  overflow: hidden;
}

.estimate-console > div > span,
.estimate-console > div > strong {
  display: block;
}

.estimate-console > div > strong {
  margin-top: 3px;
  color: #f6c76c;
  font-size: 18px;
  white-space: nowrap;
}

.estimate-console > div > strong small {
  font-size: 7px;
  font-weight: 750;
}

.estimate-console p {
  margin: 0;
  color: rgba(238, 248, 244, 0.44);
  font-size: 8px;
  line-height: 1.5;
}

.estimate-console > i {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 58px;
  height: 2px;
  background: #f6b648;
}

.estimate-console.loading > i {
  animation: estimate-pulse 0.9s ease-in-out infinite alternate;
}

@keyframes estimate-pulse {
  from { opacity: 0.25; width: 20px; }
  to { opacity: 1; width: 90px; }
}

.submission-error {
  color: #ffc1ae;
  border: 1px solid rgba(245, 117, 78, 0.28);
  background: rgba(110, 31, 18, 0.24);
}

.mobile-actions {
  position: sticky;
  bottom: 8px;
  z-index: 12;
  display: grid;
  grid-template-columns: 0.9fr 1.35fr;
  gap: 8px;
  padding: 7px;
  border: 1px solid rgba(128, 244, 214, 0.14);
  border-radius: 7px;
  background: rgba(7, 11, 10, 0.94);
  backdrop-filter: blur(16px);
}

.mobile-actions button,
.success-actions button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 0;
  height: 46px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 850;
}

.mobile-actions .ghost,
.success-actions .ghost-action {
  border: 1px solid rgba(238, 248, 244, 0.12);
  color: rgba(238, 248, 244, 0.7);
  background: rgba(238, 248, 244, 0.04);
}

.mobile-actions .primary,
.success-actions button:not(.ghost-action) {
  border: 1px solid #69dec5;
  color: #06110e;
  background: #69dec5;
}

.mobile-actions button:disabled {
  opacity: 0.42;
}

.toast-message {
  position: fixed;
  left: 50%;
  bottom: 80px;
  z-index: 50;
  width: max-content;
  max-width: calc(100vw - 28px);
  padding: 10px 13px;
  transform: translateX(-50%);
  border: 1px solid rgba(246, 182, 72, 0.28);
  border-radius: 5px;
  color: #f6d28b;
  font-size: 10px;
  text-align: center;
  background: rgba(19, 16, 9, 0.97);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.38);
}

.success-sheet {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: end center;
  padding: 12px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(8px);
}

.success-sheet > div {
  width: min(100%, 480px);
  padding: 22px 18px;
  border: 1px solid rgba(105, 222, 197, 0.25);
  border-radius: 8px;
  background: #0d1513;
}

.success-code {
  color: #65e0ad;
  font-size: 8px;
  font-weight: 900;
}

.success-mark {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  margin-top: 14px;
  border: 1px solid rgba(101, 224, 173, 0.35);
  border-radius: 6px;
  color: #65e0ad;
  font-size: 25px;
  background: rgba(101, 224, 173, 0.07);
}

.success-sheet h2 {
  margin-top: 13px;
  font-size: 21px;
}

.success-sheet p {
  margin: 7px 0 0;
  color: rgba(238, 248, 244, 0.54);
  font-size: 10px;
  line-height: 1.6;
}

.success-sheet > div > strong {
  display: block;
  margin-top: 12px;
  color: #f6b648;
  font-size: 9px;
}

.success-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 18px;
}

.step-slide-enter-active,
.step-slide-leave-active,
.sheet-up-enter-active,
.sheet-up-leave-active,
.toast-pop-enter-active,
.toast-pop-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.step-slide-enter-from,
.step-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.sheet-up-enter-from,
.sheet-up-leave-to,
.toast-pop-enter-from,
.toast-pop-leave-to {
  opacity: 0;
}

.toast-pop-enter-from,
.toast-pop-leave-to {
  transform: translate(-50%, 8px);
}

@media (max-width: 360px) {
  .mobile-task-page {
    padding: 7px;
  }

  .robot-grid,
  .action-grid {
    grid-template-columns: 1fr;
  }

  .choice-grid > button {
    min-height: 84px;
  }

  .mobile-actions,
  .success-actions {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation: none !important;
    transition: none !important;
  }
}
</style>
